import { useContext, useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { AuthContext } from '../../../../actions/auth-context';
import { expectedPhotosByProducts } from '../../../../commons/order-checklist';
import {
  canViewCapturedPhotos,
  canViewSpreadPhotos,
  isSpreadEditingOrBeyond,
} from '../../../../commons/photo-visibility';
import CapturedPhotos from './CapturedPhotos';
import EditedPhotos, {
  canUploadEditedPhotos,
  visibleEditedPhotos,
} from './EditedPhotos';
import SpreadPhotos from './SpreadPhotos';

// Aba inicial conforme o STATUS do pedido (FEAT-018): abre onde o trabalho está.
const defaultTabByStatus = (orderStatus, hasFotolivro) => {
  const capturedPhase = [
    'Em inclusão',
    'Financeiro negado',
    'Incluído',
    'Análise financeira',
    'Financeiro aprovado',
  ];
  if (capturedPhase.includes(orderStatus)) return 'captured';

  const editedPhase = ['Em edição', 'Editado', 'Inspeção de edição'];
  if (editedPhase.includes(orderStatus)) return 'edited';

  if (orderStatus === 'Edição aprovada')
    return hasFotolivro ? 'spread' : 'edited';

  const spreadPhase = [
    'Em edição de lâminas',
    'Lâminas editadas',
    'Inspeção de edição de lâminas',
    'Edição de lâminas aprovada',
  ];
  if (spreadPhase.includes(orderStatus)) return 'spread';

  // Produção em diante: a foto editada é a referência (impressão/montagem).
  return 'edited';
};

// Abas das galerias de fotos (FEAT-018): Fotos Capturadas / Fotos Editadas /
// Lâminas em ABAS com carregamento por demanda — só a aba visitada monta na
// tela (as thumbnails do S3 só são baixadas quando a aba abre). A aba ativa
// segue o status do pedido. A visibilidade de cada aba espelha as regras dos
// próprios componentes (que continuam com seus guards — defesa em camadas).
const PhotoGalleryTabs = ({ order, setOrder, getOrder }) => {
  const { hasRoles, hasPermissions } = useContext(AuthContext);

  const photos = order.photos || [];
  const capturedPhotos = photos.filter((p) => p.photo_type === 'Capturada');
  const allEditedPhotos = photos.filter((p) => p.photo_type === 'Editada');
  const spreadPhotos = photos.filter((p) => p.photo_type === 'Lâmina');

  // Impressor/plotter veem SÓ o que imprimem (05/07) — a contagem da aba
  // acompanha o recorte da galeria (mesma fonte: visibleEditedPhotos).
  const editedPhotos = visibleEditedPhotos(hasRoles, hasPermissions, order);
  const editedIsFiltered = editedPhotos.length !== allEditedPhotos.length;

  const hasProducts = (order.products || []).length > 0;
  const hasFotolivro = (order.products || []).some(
    (p) => p.type === 'Fotolivro'
  );

  const showCaptured =
    hasProducts && canViewCapturedPhotos(hasRoles, order.order_status);

  const showEdited =
    hasPermissions([
      'edit_photos',
      'edit_order',
      'edit_inspection',
      'seller_inspection',
      'print_photos',
      'plotter_photos',
    ]) &&
    (editedPhotos.length > 0 ||
      canUploadEditedPhotos(hasRoles, hasPermissions, order.order_status));

  const showSpread =
    hasFotolivro &&
    canViewSpreadPhotos(hasRoles, hasPermissions, order.order_status) &&
    (isSpreadEditingOrBeyond(order.order_status) || spreadPhotos.length > 0);

  // Contador X/N contra o esperado pelos produtos (álbum + avulsas) — vale
  // tanto para capturadas quanto para editadas. Lâminas não têm quantidade
  // esperada (varia por álbum), então mostra só o total.
  const { avulsa, albumTotal } = expectedPhotosByProducts(order);
  const expectedPhotos =
    albumTotal + Object.values(avulsa).reduce((sum, value) => sum + value, 0);

  const countLabel = (total) =>
    expectedPhotos > 0 ? `${total}/${expectedPhotos}` : `${total}`;

  const countOk = (total) =>
    expectedPhotos > 0 ? total >= expectedPhotos : null;

  const tabs = [
    showCaptured && {
      key: 'captured',
      label: 'Fotos Capturadas',
      count: countLabel(capturedPhotos.length),
      ok: countOk(capturedPhotos.length),
    },
    showEdited && {
      key: 'edited',
      label: 'Fotos Editadas',
      // Com recorte de produção o X/N não se aplica (o viewer não vê o total).
      count: editedIsFiltered
        ? `${editedPhotos.length}`
        : countLabel(editedPhotos.length),
      ok: editedIsFiltered ? null : countOk(editedPhotos.length),
    },
    showSpread && {
      key: 'spread',
      label: 'Lâminas',
      count: `${spreadPhotos.length}`,
      ok: null,
    },
  ].filter(Boolean);

  const [activeTab, setActiveTab] = useState(null);
  const [visitedTabs, setVisitedTabs] = useState([]);

  // Abre a aba da fase atual sempre que o STATUS muda (avançou/retrabalho);
  // se ela não estiver visível para o perfil, cai na primeira disponível.
  useEffect(() => {
    const wanted = defaultTabByStatus(order.order_status, hasFotolivro);
    const available = tabs.map((tab) => tab.key);
    const next = available.includes(wanted) ? wanted : available[0];

    setActiveTab(next || null);
  }, [order.order_status]);

  // Lazy: a aba só monta na primeira visita; depois fica montada (não refaz o
  // download das thumbnails ao alternar) e é apenas ocultada via d-none.
  useEffect(() => {
    if (activeTab && !visitedTabs.includes(activeTab)) {
      setVisitedTabs((state) => [...state, activeTab]);
    }
  }, [activeTab]);

  if (tabs.length === 0) return null;

  return (
    <TabsContainer className="mt-5">
      {/* Seletor de galerias com DESTAQUE acima dos demais botões (pedido do
          dono 05/07): maiores, em negrito, ativo no amarelo da marca. */}
      <div className="d-flex flex-wrap justify-content-center gap-2 tabs-bar">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`btn btn-lg tab-btn ${
              activeTab === tab.key ? 'active' : ''
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}{' '}
            <span
              className="tab-count"
              style={{
                backgroundColor:
                  tab.ok === null ? '#464646' : tab.ok ? '#1b5e20' : '#856404',
              }}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {showCaptured && visitedTabs.includes('captured') && (
        <div className={activeTab === 'captured' ? '' : 'd-none'}>
          <CapturedPhotos order={order} setOrder={setOrder} />
        </div>
      )}

      {showEdited && visitedTabs.includes('edited') && (
        <div className={activeTab === 'edited' ? '' : 'd-none'}>
          <EditedPhotos order={order} setOrder={setOrder} getOrder={getOrder} />
        </div>
      )}

      {showSpread && visitedTabs.includes('spread') && (
        <div className={activeTab === 'spread' ? '' : 'd-none'}>
          <SpreadPhotos order={order} setOrder={setOrder} getOrder={getOrder} />
        </div>
      )}
    </TabsContainer>
  );
};

PhotoGalleryTabs.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.string,
    order_status: PropTypes.string,
    photos: PropTypes.arrayOf(Object),
    products: PropTypes.arrayOf(Object),
  }).isRequired,
  setOrder: PropTypes.func.isRequired,
  getOrder: PropTypes.func.isRequired,
};

export default PhotoGalleryTabs;

const TabsContainer = styled.section`
  .tabs-bar {
    border-bottom: 2px solid #d0d0d0;
    padding-bottom: 14px;

    /* Destaque acima dos demais botões (pedido do dono 05/07): maiores, em
       negrito e no par de cores da marca — ativo AMARELO com texto escuro,
       inativos escuros. Nenhum outro botão da tela usa esse visual. */
    .tab-btn {
      text-transform: uppercase;
      font-weight: 700;
      font-size: 15px;
      letter-spacing: 0.5px;
      padding: 10px 22px;
      background-color: #464646;
      border: 2px solid #464646;
      color: #fff;

      :hover {
        background-color: #5a5a5a;
        color: #fff;
      }

      &.active {
        background-color: #ffba00;
        border-color: #ffba00;
        color: #212529;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
      }

      .tab-count {
        color: #fff;
        font-weight: 700;
        font-size: 12px;
        padding: 2px 9px;
        border-radius: 10px;
        margin-left: 6px;
      }
    }
  }

  /* As galerias já têm o próprio espaçamento (mt-5); dentro das abas o topo
     fica mais colado no seletor. */
  section {
    margin-top: 12px !important;
  }
`;
